import {
  DefaultGlobalNotFound,
  Outlet,
  createRootRoute,
} from "@tanstack/react-router"
import { meta } from "@/content"
import { withProfiler } from "@/monitoring"
import { Root } from "@/base"
import appCss from "@/index.css?url"
// Importing @/i18n also initialises i18next (initReactI18next needs no provider)
import { FALLBACK_LOCALE, SUPPORTED_LOCALES, htmlLang, isLocale } from "@/i18n"

export const Route = createRootRoute({
  head: ({ matches }) => {
    const pathname = matches[matches.length - 1]?.pathname ?? "/"
    const canonicalUrl = `https://profiling.app${pathname}`

    const links: Array<{ rel: string; href: string; hrefLang?: string }> = [
      { rel: "stylesheet", href: appCss },
      { rel: "canonical", href: canonicalUrl },
    ]

    // Public pages are locale-prefixed (/{locale}/…); advertise the alternates
    // (with BCP-47 hreflang tags) so search engines pair the language versions.
    const [, segment = "", rest = ""] =
      /^\/([^/]+)(\/.*)?$/.exec(pathname) ?? []
    if (isLocale(segment)) {
      for (const locale of SUPPORTED_LOCALES) {
        links.push({
          rel: "alternate",
          hrefLang: htmlLang(locale),
          href: `https://profiling.app/${locale}${rest}`,
        })
      }
      links.push({
        rel: "alternate",
        hrefLang: "x-default",
        href: `https://profiling.app/${FALLBACK_LOCALE}${rest}`,
      })
    }

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "initial-scale=1, width=device-width" },
        { name: "theme-color", content: "#475569" },
        ...meta({
          title: "Profiling.app",
        }),
      ],
      links,
      scripts: [
        { src: "/env.js" },
        {
          // Load Huginn (Umami) and route every event through maskTenant, which
          // strips the /t/<tenantSlug> segment from url + referrer to /t/[id]
          // before send so tenant slugs never reach analytics (issue #92).
          children:
            '(function(){window.maskTenant=function(t,p){if(p){var r=new RegExp("/t/[^/?#]+","g");if(p.url)p.url=p.url.replace(r,"/t/[id]");if(p.referrer)p.referrer=p.referrer.replace(r,"/t/[id]")}return p};var e=window.env;if(e&&e.HUGINN_ID){var s=document.createElement("script");s.defer=true;s.src="https://huginn.motley.dev/huginn.js";s.dataset.websiteId=e.HUGINN_ID;s.dataset.beforeSend="maskTenant";document.head.appendChild(s)}})()',
        },
      ],
    }
  },
  component: withProfiler(() => (
    <Root>
      <Outlet />
    </Root>
  )),
  notFoundComponent: DefaultGlobalNotFound,
})
